<template>
  <div class="min-h-full flex flex-col bg-white">
    <div v-if="loading" class="flex-1 flex flex-col">
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex gap-3">
          <div v-for="n in 2" :key="'sk-card-' + n" class="flex-1 rounded-lg p-3 bg-gray-50">
            <div class="h-2.5 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div class="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="h-2.5 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="space-y-1.5">
          <div class="h-3 bg-gray-100 rounded animate-pulse w-full"></div>
          <div class="h-3 bg-gray-100 rounded animate-pulse w-4/5"></div>
        </div>
      </div>
    </div>

    <div v-else-if="!assessment" class="flex-1 flex items-center justify-center px-6">
      <div class="text-center">
        <FontAwesomeIcon :icon="['fas', 'layer-group']" class="text-2xl text-gray-300 mb-2" />
        <p class="text-xs text-gray-400">No risk tranche assessment for this patient</p>
      </div>
    </div>

    <template v-else>
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex gap-3">
          <div class="flex-1 rounded-lg p-3 bg-gray-50">
            <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Risk Tranche</div>
            <div class="mt-1.5 flex items-center gap-1.5 flex-wrap">
              <span class="inline-block px-2 py-0.5 rounded-full text-xs font-medium" :class="trancheStyle(assessment.risk_tranche).pill">
                {{ trancheLabel(assessment.risk_tranche) }}
              </span>
              <span
                v-if="assessment.risk_tranche_confidence"
                class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="confidenceClass(assessment.risk_tranche_confidence)"
              >{{ assessment.risk_tranche_confidence }} confidence</span>
              <span
                v-if="assessment.acute_horizon"
                class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="horizonStyle(assessment.acute_horizon)"
              >{{ horizonLabel(assessment.acute_horizon) }}</span>
            </div>
            <div v-if="assessment.risk_score != null" class="mt-1.5 text-[11px] text-gray-500">
              Risk score <span class="font-semibold text-gray-800 tabular-nums">{{ assessment.risk_score }}</span>
              <span class="text-gray-400"> of 100, an ordering within the tranche</span>
            </div>
          </div>
          <div class="flex-1 rounded-lg p-3 bg-gray-50">
            <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Intervention Category</div>
            <div class="text-sm font-medium mt-1.5 leading-tight text-gray-700">
              {{ categoryLabel(assessment.intervention_category) }}
            </div>
          </div>
        </div>
      </div>

      <div class="px-4 py-3 border-b border-gray-100">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Rationale</div>
        <DemoText tag="p" class="text-xs text-gray-700 leading-relaxed" :text="assessment.rationale.text" :record="patientIdentifier" />
        <EvidenceList :items="assessment.rationale.evidence" :record="patientIdentifier" />
      </div>

      <div class="px-4 py-3 border-b border-gray-100">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Next Best Actions</div>
        <ol class="space-y-2">
          <li
            v-for="(item, i) in assessment.next_best_actions"
            :key="i"
            class="flex gap-2 text-xs text-gray-700 leading-relaxed"
          >
            <span class="shrink-0 w-4 h-4 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold flex items-center justify-center mt-0.5">{{ i + 1 }}</span>
            <div class="min-w-0 flex-1">
              <DemoText tag="span" :text="item.action" :record="patientIdentifier" />
              <span
                v-if="item.confidence"
                class="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium align-middle"
                :class="confidenceClass(item.confidence)"
              >{{ item.confidence }}</span>
              <EvidenceList :items="item.evidence" :record="patientIdentifier" />
            </div>
          </li>
        </ol>
      </div>

      <div class="px-4 py-2 text-[10px] text-gray-400 flex items-center gap-3">
        <span>Assessed {{ formatDate(assessment.scored_at) }}</span>
        <span v-if="assessment.model">{{ assessment.model }}</span>
        <span v-if="assessment.has_graph === false">EHR data only, no claims record</span>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EvidenceList from '@/components/Care_Management/Widgets/PatientEngagement/EvidenceList.vue';
import { TRANCHE_LABELS, TRANCHE_STYLES, CATEGORY_LABELS, CONFIDENCE_STYLES, HORIZON_LABELS, HORIZON_STYLES } from '@/components/Care_Management/riskTranche';
import DemoText from '@/components/DemoMode/DemoText.vue';

export default {
  name: 'AIRiskDetail',
  components: { DemoText, FontAwesomeIcon, EvidenceList },

  props: {
    patientIdentifier: { type: String, default: null }
  },

  data() {
    return {
      loading: false,
      assessment: null
    };
  },

  watch: {
    patientIdentifier: {
      immediate: true,
      handler: 'fetchAssessment'
    }
  },

  methods: {
    async fetchAssessment() {
      if (!this.patientIdentifier) {
        this.assessment = null;
        return;
      }

      this.loading = true;
      try {
        const { data } = await axios.get('/api/care-management/risk-tranche/', {
          params: { patient_identifier: this.patientIdentifier }
        });
        this.assessment = data.found ? data : null;
      } catch {
        this.assessment = null;
      } finally {
        this.loading = false;
      }
    },

    trancheLabel(key) {
      return TRANCHE_LABELS[key] || key;
    },

    trancheStyle(key) {
      return TRANCHE_STYLES[key] || TRANCHE_STYLES.no_intervention_necessary;
    },

    categoryLabel(key) {
      return CATEGORY_LABELS[key] || key;
    },

    confidenceClass(level) {
      return CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.low;
    },

    horizonLabel(key) {
      return HORIZON_LABELS[key] || key;
    },

    horizonStyle(key) {
      return HORIZON_STYLES[key] || '';
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
};
</script>
